import {inject, Injectable} from '@angular/core';
import {AConst} from './a-const.enum';
import {TranslateService} from '@ngx-translate/core';
import {ObjectFieldTraverseService, TraverseObjectResult} from './object-field-traverse.service';
import {MetaField} from './definitions/meta-field';
import {SuperObjectModel} from './definitions/super-object-model';
import {UserData} from './definitions/user-data';
import {BaseModel} from './definitions/base-model';
import {FieldInputType} from './definitions/field-input-type.enum';
import {FieldType} from './definitions/field-type.enum';
import {LoggerService} from "./logger.service";
import {SearchReferenceService} from "./search-reference.service";
import {FieldDateInfoService} from "./field-date-info.service";
import {FieldKeyService} from "./field-key.service";

@Injectable({
  providedIn: 'root'
})
export class FieldStateService {
  // Injections
  private readonly fieldDateInfoService: FieldDateInfoService = inject(FieldDateInfoService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly objectFieldTraverse: ObjectFieldTraverseService = inject(ObjectFieldTraverseService);
  private readonly searchReferenceService: SearchReferenceService = inject(SearchReferenceService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly fieldKeyService: FieldKeyService = inject(FieldKeyService);

  isEditable(rootObject: SuperObjectModel, object: BaseModel, field: MetaField, userData: UserData): boolean {
    let res = false;
    let createdById = rootObject.created_by_id;
    if (!createdById && object) {
      createdById = object.created_by_id;
    }
    const userId = userData.artifact_id;

    const edit = field.edit;
    const value = object ? object[field.name] : null;
    switch (edit) {
      case undefined:
      case null:
      case 'no_edit':
        break;
      case AConst.EDIT:
        res = true;
        break;
      case AConst.EDIT_OWNER:
        res = !createdById || createdById === userId;
        break;
      case AConst.EDIT_ONCE:
        res = !value || !createdById || object.$$justAdded;
        break;
      case AConst.EDIT_ONCE_NOT_STORED:
        res = !object[field.name] && !object.artifact_id;
        break;
      default:
        this.logger.warn('Unable to handle edit type ' + edit);

    }
    return res;
  }

  getFieldInputType(field: MetaField, rootObject: SuperObjectModel, object: BaseModel, userData: UserData): string {
    let res: string;
    const editable = this.isEditable(rootObject, object, field, userData);
    if (editable) {
      const reference = this.searchReferenceService.getSearchReferenceFromField(field, rootObject);
      const dateInfo = this.fieldDateInfoService.getFieldDateInfo(field);
      switch (field.input_type) {
        case FieldInputType.MAP_ID:
        case FieldInputType.REF_ARRAY:
          res = FieldInputType.MAP_ID;
          if (reference.is_hierarchic) {
            res = FieldInputType.HIERARCHIC_SELECT;
          }
          break;
        case FieldInputType.TEXT_AREA:
          res = FieldInputType.TEXT_AREA;
          break;
        case 'select':
          res = 'select';
          break;
        case FieldInputType.INPUT:
          res = 'text';
          break;
        case FieldInputType.DATE_TIME_ISO:
        case FieldInputType.DATE_ISO:
          if (dateInfo?.to_date_field) {
            res = FieldInputType.DATE_TIME_ISO_RANGE;
          } else if (dateInfo?.is_to_date_field) {
            res = null;
          } else {
            res = field.input_type;
          }
          break;
        case FieldInputType.IDENTIFIER:
          res = FieldInputType.IDENTIFIER;
          break;
        case FieldInputType.NUMBER:
          res = FieldInputType.NUMBER;
          break;
        case FieldInputType.CHECKBOX:
          res = FieldInputType.CHECKBOX;
          break;
        case FieldInputType.CHECK_ARRAY:
          res = FieldInputType.CHECK_ARRAY;
          break;
        case FieldInputType.RADIO_INLINE_ARRAY:
          res = FieldInputType.RADIO_INLINE_ARRAY;
          break;
        case FieldInputType.RADIO_OPTION:
          res = FieldInputType.RADIO_OPTION;
          break;
        case FieldInputType.SEARCH_SELECTOR:
          res = FieldInputType.SEARCH_SELECTOR;
          break;
        case FieldInputType.SEARCH_SELECTOR_MULTIPLE:
          res = FieldInputType.SEARCH_SELECTOR_MULTIPLE;
          break;
        case FieldInputType.PASSWORD:
          res = FieldInputType.PASSWORD;
          break;
        case FieldInputType.IMAGE:
          res = FieldInputType.IMAGE;
          break;
        case FieldInputType.META_OPERATION_FIELD:
          res = FieldInputType.META_OPERATION_FIELD;
          break;
        default:
          res = 'text';
      }
    } else if (field.field_type === FieldType.OBJECT_USAGE) {
      res = FieldInputType.OBJECT_USAGE;
    } else {
      res = 'display';
    }
    return res;
  }

  getFieldTitle(field: MetaField, rootObject: BaseModel, translate: boolean, getPlaceholder: boolean): string {
    const traverseRes = this.objectFieldTraverse.traverseObjectByPath(rootObject, field.path, 1);
    let title = this.getTitleFromSubObject(traverseRes, field, getPlaceholder);
    title = title || field.title || field.field_title;
    title = Array.isArray(title) ? title[0] : title;
    if (title) {
      title = translate ? this.translate.instant(title) : title;
    }
    return title;
  }

  getSubObject(rootObject: BaseModel, field: MetaField, index?: number, parentIndex?: number, grandparentIndex?: number): BaseModel | BaseModel[] {
    let fieldPath: string;
    if (index === undefined) {
      fieldPath = this.fieldKeyService.getFieldKeyWhileDrawingInputs(field, parentIndex);
    } else if (grandparentIndex === undefined) {
      fieldPath = this.fieldKeyService.getFieldKeyWhileDrawingInputs(field, index, parentIndex);
    } else {
      fieldPath = this.fieldKeyService.getFieldKeyWhileDrawingInputs(field, index, parentIndex, grandparentIndex);
    }
    const traverseRes = this.objectFieldTraverse.traverseObjectByPath(rootObject, fieldPath, 1);
    return traverseRes.subObject
  }

  getParentSubObject(rootObject: BaseModel, field: MetaField, index: number, parentIndex: number, grandparentIndex?: number): TraverseObjectResult {
    let fieldPath: string;
    if (index === undefined) {
      fieldPath = this.fieldKeyService.getFieldKeyWhileDrawingInputs(field, parentIndex);
    } else if (grandparentIndex === undefined) {
      fieldPath = this.fieldKeyService.getFieldKeyWhileDrawingInputs(field, index, parentIndex);
    } else {
      fieldPath = this.fieldKeyService.getFieldKeyWhileDrawingInputs(field, index, parentIndex, grandparentIndex);
    }
    return this.objectFieldTraverse.traverseObjectByPath(rootObject, fieldPath, 1);
  }


  /**
   * Checks whether a field is an "edit once" field and if so, returns "true" if it has a value.
   * @param field
   * @param rootObject
   * @param object
   */
  checkEditOnce(field: MetaField, rootObject: BaseModel, object: BaseModel) {
    let res = false;
    let createdById = rootObject.created_by_id;
    if (field.edit === AConst.EDIT_ONCE) {
      res = object[field.name] !== null && object[field.name] !== undefined && createdById !== null;
    }
    return res;
  }

  getInlineFieldName(metaField: MetaField) {
    let res = metaField.name;
    if (metaField.inline) {
      if (metaField.inline.prop) {
        res = metaField.inline.prop;
      } else if (metaField.inline.unique_props?.length) {
        res = metaField.inline.unique_props[0]
      } else {
        this.logger.warn(`Unable to get inline field name for ${metaField.name}`);
      }
    }
    return res;
  }

  private getTitleFromSubObject(traverseRes: TraverseObjectResult, field: MetaField, getPlaceholder: boolean) {
    let title: string;
    if (traverseRes.subObject) {
      const parentField: MetaField = traverseRes.subObject['$$meta'][traverseRes.fieldName];
      if (!getPlaceholder && parentField?.title && parentField.field_type !== FieldType.ARRAY && parentField.input_type !== FieldInputType.META_OPERATION_FIELD) {
        const dateInfo = this.fieldDateInfoService.getFieldDateInfo(field);
        const isHorizontalDate = dateInfo?.view === 'horizontal';
        const parentFieldName = this.getInlineFieldName(parentField);
        const hasMatchingInlineProp = parentField.inline && field.name === parentFieldName;
        title = isHorizontalDate || hasMatchingInlineProp ? parentField.title : null;
      }
    }
    return title;
  }


}
